import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import useCurrencyCode from '@alltrails/context/hooks/useCurrencyCode';
import usePlans from '@alltrails/context/hooks/usePlans';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';

// Net-new subheading for the generic upsell modals. Matches the 2026 upsell designs:
// the annual price renders in Medium weight and the trial copy drops the monthly breakdown.
// Design: https://www.figma.com/design/5lZfiEveGScwx75JKpB12W/Upsell-XPs-2026?node-id=4843-18095&m=dev
const GenericUpsellTierSubheading = ({ tier }: { tier: PurchaseTier }) => {
  const currencyCode = useCurrencyCode();
  const plans = usePlans();
  const plan = plans[tier];
  const user = useUser();

  const isPromoEligible = user?.isPromoEligible;
  const bold = (chunks: ReactNode) => (
    <Typography component="span" variant="text200bold">
      {chunks}
    </Typography>
  );
  const annualCost = <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />;
  const monthlyCost = <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />;

  function getText() {
    if (isPromoEligible) {
      return <FormattedMessage defaultMessage="7 days free, then <b>{annualCost}</b>/year" values={{ annualCost, b: bold }} />;
    }

    if (user?.canUpgradeExistingSubscription && !user?.subscription?.trial) {
      return (
        <FormattedMessage
          defaultMessage="<b>{annualCost}</b>/year — and we’ll <b>refund</b> the remainder of your current plan"
          values={{ annualCost, b: bold }}
        />
      );
    }

    return <FormattedMessage defaultMessage="<b>{annualCost}</b>/year (that’s {monthlyCost}/month)" values={{ annualCost, b: bold, monthlyCost }} />;
  }

  return (
    <Typography component="div" variant="text200">
      {getText()}
    </Typography>
  );
};

export default GenericUpsellTierSubheading;
